import { PartialNullable } from "../../Extensions";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ApplicationInformationService } from "../../RadWindow";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ServiceManager } from "../../ServiceManager";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { IControl } from "./Control";
import { ActionButtonMixin, ActionButtonMixinPropertyValues, IActionButtonMixin } from "./Mixins/ActionButtonMixin";
import { NAMESPACE } from "./_Namespace";

export type LinkPropertyValues =
    ContainerControlPropertyValues
    & ActionButtonMixinPropertyValues
    & {
        disableSelfOnClick: boolean;
        href: string;
        target: string;
    };

export type LinkProperties = {
    [name in keyof LinkPropertyValues]: Property<LinkPropertyValues[name]>
};

export interface ILink extends IContainerControl, LinkProperties, IActionButtonMixin
{
}

export class Link extends ContainerControl implements ILink
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Link";
    public static override className = "Link";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<LinkPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                disableSelfOnClick: false,
                href: StringHelper.empty,
                tabStop: true,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _textElementRef!: ElementRef<HTMLSpanElement>;

    // #endregion

    // #region Properties

    public disableSelfOnClick!: PropertyBoolean;
    public href!: PropertyString;
    public target!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<LinkPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-link");

        const
            href = this.$$element.get()?.attr("href") as string | undefined;

        if (href)
            this.href.set(href, false);

        // Trigger Property Changes
        this.href.triggerChange();
        this.target.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public click()
    {
        if (!this.enabled.get())
            return;

        this.trigger("click");
    }

    // #endregion

    // #region Protected Methods

    protected _disableSelfOnClickChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _hrefChanged(change: PropertyChange<string>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        if (change.currentValue)
        {
            const
                applicationInformation = ServiceManager.getRequired(ApplicationInformationService);

            $$element.attr(
                "href",
                change.currentValue.startsWith("/") && !change.currentValue.startsWith(applicationInformation.virtualDirectory)
                    ? applicationInformation.virtualDirectory + change.currentValue.substring(1)
                    : change.currentValue
            );
        }
        else
            $$element.removeAttr("href");
    }
    protected _targetChanged(change: PropertyChange<string>)
    {
        this.$$element.get()?.attr("target", change.currentValue);
    }

    // #endregion

    // #region Overrides

    // #region Methods

    protected override __initializeProperties(propertyValues: LinkPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._textElementRef = new ElementRef();

        this.disableSelfOnClick = new PropertyBoolean(this, propertyValues.disableSelfOnClick, this._disableSelfOnClickChanged);
        this.href = new PropertyString({
            context: this,
            initialValue: propertyValues.href,
            changeHandler: this._hrefChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Url" }),
            ],
        });
        this.target = new PropertyString(this, propertyValues.target, this._targetChanged);

        this.action = new PropertyString(this, propertyValues.action, this._actionChanged);
        this.actionArguments = new PropertyString(this, propertyValues.actionArguments, this._actionArgumentsChanged);
        this.actionTarget = new Property<IControl>(this, "System.Object, System.Private.CoreLib", propertyValues.actionTarget, this._actionTargetChanged);
        this.actionTargetControlClass = new PropertyString(this, propertyValues.actionTargetControlClass, this._actionTargetControlClassChanged);
        this.actionTargetId = new PropertyString(this, propertyValues.actionTargetId, this._actionTargetIdChanged);
        this.actionTargetSelector = new PropertyString(this, propertyValues.actionTargetSelector, this._actionTargetSelectorChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this.on("mousedown touchstart", { self: this }, this.onMouseDownOrTouchStart);
        this.on("keyup", { self: this }, this.onKeyUp);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <a>
                <span ref={this._textElementRef} class="rad-text"></span>
                {children ?? this._createChildrenHtml()}
            </a>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this._textElementRef.current.text(change.currentValue);
    }

    // #endregion

    // #region Event Handers

    protected override onClick(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.focus(); // Ensure button has focus (causes other changed events to fire)

        if (self.disableSelfOnClick.get())
            self.enabled.set(false);

        ActionButtonMixin.processActionButtonClick(self, evt);
    }

    // #endregion

    // #endregion

    // #region IActionButtonMixin

    // #region Properties

    public action!: PropertyString;
    public actionArguments!: PropertyString;
    public actionTarget!: Property<IControl>;
    public actionTargetControlClass!: PropertyString;
    public actionTargetId!: PropertyString;
    public actionTargetSelector!: PropertyString;

    // #endregion

    // #region Methods

    protected _actionChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionArgumentsChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetChanged(change: PropertyChange<IControl>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetControlClassChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetIdChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _actionTargetSelectorChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #endregion

    // #region Event Handlers

    protected onKeyUp(evt: IJNativeEvent<KeyboardEvent>)
    {
        const
            self = evt.data.self as this;

        evt.stopPropagation();

        if (evt.originalEvent.key === "Enter")
        {
            evt.preventDefault();
            self.click();
        }
    }
    protected onMouseDownOrTouchStart(evt: IJNativeEvent)
    {
        evt.stopPropagation();
    }

    // #endregion

    // #endregion
}